import React, {useLayoutEffect, useMemo, useRef, useState} from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import ButtonBase from "connectui/component/input/button/base/ButtonBase";
import {replaceURLs} from "utilities/helperFunctions";
import {useFeatureFlags} from "utilities/featureUtil";

const useStyles = (theme) =>
  buildStyles({
    bubble: {
      padding: theme.spacing(2.5, 3.5),
      borderRadius: `${theme.radius.lg}px`,
    },
    messageCollapsed: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      width: "100%",
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      wordBreak: "break-word",
      whiteSpace: "break-spaces",
      WebkitLineClamp: 10,
    },
    message: {
      wordBreak: "break-word",
      whiteSpace: "break-spaces",
    },
    expandButton: {
      backgroundColor: "transparent",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      border: "none",
      outline: "none",
      boxShadow: "none",
      padding: 0,
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(2.5),
    },
  });

const MessageContentBase = ({
  isGuestSender,
  isHostSender,
  translation,
  message,
  ...props
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const containerRef = useRef(null);
  const {emailPlainTextFix} = useFeatureFlags();
  const [expanded, setExpanded] = useState(true);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const [collapsedMessageClass, setCollapsedMessageClass] = useState("");

  const messageContent = useMemo(
    () => replaceURLs(translation ?? message.message, emailPlainTextFix),
    [translation, message],
  );

  const formatUrl = (url) => {
    if (!url.match(/^https?:\/\//i)) {
      return `https://${url}`;
    }
    return url;
  };

  useLayoutEffect(() => {
    if (containerRef.current) {
      const lineHeight = parseInt(theme.typography.textSM.regular.lineHeight);
      const containerHeight = containerRef.current.scrollHeight;
      // Collapse if more than 20 lines of text
      if (containerHeight > lineHeight * 20) {
        setCollapsedMessageClass(classes.messageCollapsed);
        setShowExpandButton(true);
        setExpanded(false);
      }
    }
  }, [containerRef]);

  return (
    <FlexContainer
      ref={containerRef}
      flexDirection="column"
      justifyContent={isGuestSender ? "flex-start" : "flex-end"}
      className={classes.bubble}
      sx={{
        borderStartStartRadius: `${isGuestSender ? 0 : theme.radius.lg}px`,
        borderStartEndRadius: `${isGuestSender ? theme.radius.lg : 0}px`,
        backgroundColor: isHostSender
          ? theme.colors.primary600
          : theme.colors.gray200,
      }}
    >
      <Text
        variant="textSM"
        color={isHostSender ? "white" : "textPrimary"}
        className={expanded ? classes.message : collapsedMessageClass}
      >
        {messageContent.map((txt, index) => {
          if (txt.type === "link") {
            return (
              <Text
                key={index}
                as={"a"}
                href={isMobile ? "#" : formatUrl(txt.text)}
                target="_blank"
                rel="noopener noreferrer"
                variant="textSM"
                color={isGuestSender ? "primary" : "primary100"}
                onClick={() => {
                  if (isMobile) {
                    window.open(txt.text, "_system", "location=yes");
                  }
                }}
                sx={{textDecoration: "underline"}}
              >
                {txt.text}
              </Text>
            );
          }
          return txt.text;
        })}
      </Text>
      {showExpandButton && (
        <ButtonBase
          className={classes.expandButton}
          onClick={() => setExpanded(!expanded)}
        >
          <Text
            variant="textSM"
            weight="semibold"
            color={isGuestSender ? "primary" : "white"}
          >
            {expanded ? "Hide Full Message" : "Show Full Message"}
          </Text>
          <SvgIcon
            component={ICONS[expanded ? "chevron-up" : "chevron-down"]}
            sx={{
              width: "12px",
              height: "12px",
              color: isGuestSender ? theme.colors.primary : theme.colors.white,
              paddingTop: theme.spacing(0.5),
            }}
          />
        </ButtonBase>
      )}
    </FlexContainer>
  );
};

export default MessageContentBase;
